import { useEffect, useMemo } from "react";
import { Drawer } from "@design-system/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useOrganizationServices } from "@api/organizationService.api";
import { CloseTemplateTasksDrawerContent } from "./CloseTemplateTasksDrawerContent";
import { ApplyCloseTemplateDrawer } from "./ApplyCloseTemplateDrawer";
import { useCloseTemplateTasks } from "./useCloseTemplateTasks";
import { CloseTemplateTasksDrawerProps } from "./closeTemplateTasksDrawer.types";

/**
 * Shell — owns the Drawer frame and data fetching.
 * Defers rendering CloseTemplateTasksDrawerContent until both the stages
 * and the template's tasks have resolved, so the useState initialiser inside
 * Content always sees real data (no useEffect bridge required).
 */
export const CloseTemplateTasksDrawer = ({
    open,
    closeTemplateId,
    onClose,
}: CloseTemplateTasksDrawerProps) => {
    const { data: services } = useOrganizationServices();

    const {
        localTasks,
        stages,
        currentTemplate,
        isReady,
        isDirty,
        isSaving,
        isConfirmDiscardOpen,
        isApplyDrawerOpen,
        setIsApplyDrawerOpen,
        handleLocalTasksChange,
        handleSave,
        handleSaveAndApply,
        handleOpenApplyDrawer,
        handleCloseAttempt,
        handleConfirmDiscard,
        handleSaveAndExit,
        handleKeepEditing,
    } = useCloseTemplateTasks({
        closeTemplateId,
        isFetchEnabled: open,
        onClose,
    });

    const drawerTitle = useMemo(() => {
        if (!currentTemplate) {
            return "Manage tasks";
        }
        const service = (services ?? []).find(
            (s) => s.id === currentTemplate.serviceId,
        );
        return service
            ? `${currentTemplate.name} · ${service.name}`
            : currentTemplate.name;
    }, [currentTemplate, services]);

    useEffect(() => {
        if (!open) {
            setIsApplyDrawerOpen(false);
        }
    }, [open, setIsApplyDrawerOpen]);

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                onCloseAttempt={handleCloseAttempt}
                direction="right"
                size="fullScreen"
                noPadding
                dismissible={!isSaving}
                headerAddonLeft={drawerTitle}
            >
                {open && !isReady && <Loader />}
                {isReady && stages && (
                    <CloseTemplateTasksDrawerContent
                        key={closeTemplateId}
                        localTasks={localTasks}
                        onLocalTasksChange={handleLocalTasksChange}
                        stages={stages}
                        isSaving={isSaving}
                        isDirty={isDirty}
                        onSave={handleSave}
                        onSaveAndApply={handleSaveAndApply}
                        onApply={handleOpenApplyDrawer}
                        basis={currentTemplate?.basis ?? null}
                        workspaceId={currentTemplate?.workspaceId ?? null}
                    />
                )}
            </Drawer>

            {isApplyDrawerOpen && (
                <ApplyCloseTemplateDrawer
                    key={currentTemplate?.id ?? "none"}
                    open={isApplyDrawerOpen}
                    closeTemplate={currentTemplate}
                    onClose={() => setIsApplyDrawerOpen(false)}
                    onApplyComplete={() => {
                        setIsApplyDrawerOpen(false);
                        onClose();
                    }}
                />
            )}

            <ConfirmationModal
                show={isConfirmDiscardOpen}
                title="Unsaved changes"
                question="You have unsaved changes. What would you like to do?"
                yes="Discard changes"
                yesBtnVariant="destructive"
                no="Save changes & exit"
                noBtnVariant="secondary"
                onConfirm={handleConfirmDiscard}
                onReject={handleSaveAndExit}
                onHide={handleKeepEditing}
            />
        </>
    );
};
